import type { Operations, Operation } from './api.generated';
import type { Schemas } from './api.generated';
import type { WorkspaceData } from './data';
import { ADMIN_ROUTES_BY_SCREEN } from '../../app/routes';
import type { AdminNavigationScreenId } from '../../app/routes';
export type ScreenState = 'ready' | 'loading' | 'empty' | 'error' | 'denied' | 'expired' | 'not-found' | 'partial' | 'conflict';
export type ScreenId = AdminNavigationScreenId;
export type Command = { [K in Operation]: { operation: K; body: Operations[K]['body']; query?: Operations[K]['query']; params?: Record<string,string|number> } }[Operation];
export interface WorkspaceProps {
  data: WorkspaceData;
  actor: { id: number; loginId: string; name: string; authorities: readonly string[] };
  onSession: (session: Schemas['AuthenticateAdminResponse'] | null) => void;
  onDownload: (fileAssetId: number, context: Schemas['AdminFileDownloadRequest']) => Promise<Blob>;
  onUpload: (file: File) => Promise<Schemas['FileOperationResponse']>;
  allowAmountAdjustment?: boolean;
  screen: ScreenId;
  state?: ScreenState;
  readOnly?: boolean;
  scenario?: string;
  selectedId?: number;
  memory?: Record<string, unknown>;
  onNavigate: (screen: ScreenId, selectedId?: number) => void;
  onRefresh?: () => void;
  onRemember?: (key: string, value: unknown) => void;
  onCommand: (command: Command) => Promise<unknown>;
}
export const titles = Object.fromEntries(
  Object.entries(ADMIN_ROUTES_BY_SCREEN).map(([screen, definition]) => [screen, definition.title]),
) as Record<ScreenId, string>;
export const ceilWonAmount = (amount: number): number => Math.ceil(amount);
export const money = (n: number | null | undefined) => {
  if (n == null || !Number.isFinite(n)) return '—';
  const amount = (n < 0 ? -1 : 1) * ceilWonAmount(Math.abs(n));
  return `${amount.toLocaleString('ko-KR')}원`;
};
export const date = (value: string | null | undefined) => value ? new Intl.DateTimeFormat('ko-KR',{timeZone:'Asia/Seoul',year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit',hour12:false}).format(new Date(/Z$|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`)) : '—';
export const bankName = (code: string | null | undefined) => ({'004':'KB국민은행','020':'우리은행'}[code ?? ''] ?? code ?? '—');
export const documentTypeLabel = (code: string, serverName?: string | null) => serverName?.trim()
  || ({LEASE_CONTRACT:'임대차계약서',BILL:'청구서',MEDICAL_RECEIPT:'진료비 영수증'}[code] ?? code.replaceAll('_',' '));
export const contentTypeLabel = (code: string) => ({NOTICE:'공지',FAQ:'FAQ',GUIDE:'이용가이드'}[code] ?? code);
const businessCodeLabels: Record<string,string> = {
  SKT:'SKT', KT:'KT', LGU:'LGU+', SKT_MVNO:'SKT알뜰폰', KT_MVNO:'KT알뜰폰', LGU_MVNO:'LGU+알뜰폰',
  SIGNUP:'가입', APPROVAL:'승인', CANCEL:'취소', PLAN_CHANGE:'요금제 변경',
  REQUESTED:'요청', RECEIVED:'접수', IN_PROGRESS:'처리 중', ANSWERED:'답변 완료', CLOSED:'종료',
  APPROVAL_PENDING:'승인 대기', APPROVED:'승인 완료', PAID:'지급 완료', REJECTED:'반려',
  PROCESSING:'처리 중', COMPLETED:'완료', FAILED:'실패', CANCELED:'취소', READY:'대기', UNKNOWN:'결과 확인 중',
  VERIFIED:'인증 완료', CONSUMED:'사용 완료', ABANDONED:'인증 중단', EXPIRED:'기간 만료',
  RENT_FRAUD:'월세 사기', INSURANCE_LAPSE:'보험 실효', OTHER_DAMAGE:'기타 피해',

  PROVIDER_REJECTED: '외부 기관 거절',
  ADMIN_CANCEL: '관리자 취소',
  ACCIDENT: '사고 의료비',
  TRANSACTION_APPROVED: '거래 심사 승인·결제 안내',
  TRANSACTION_SUPPLEMENT_REQUIRED: '거래 자료 보완 요청',
  TRANSACTION_REJECTED: '거래 심사 반려',
  TRANSACTION_PAYMENT_APPROVED: '카드 결제 완료·송금 선택 안내',
  TRANSACTION_COMPLETED: '예약 송금 완료',
  TRANSACTION_TRANSFER_FAILED: '예약 송금 실패',
  TRANSACTION_CARD_PAYMENT_CANCELED: '카드 결제 취소',
  BENEFIT_APPROVED: '혜택지원 승인',
  BENEFIT_REJECTED: '혜택지원 반려',
  BENEFIT_PAID: '혜택지원 지급',
  RECURRING_PAYMENT_APPROVED: '월정액 결제 완료',
  RECURRING_PAYMENT_FAILED: '월정액 결제 실패·해지 사전 안내',
  SUBSCRIPTION_CANCELED: '월정액 자동 해지',
  SUBSCRIPTION_PLAN_CHANGED: '월정액 상품 변경',
  TERMINATION_COMPLETED: '해지·환불 완료',
  INQUIRY_ANSWERED: '1:1 문의 답변',
  DEFAULT: '기본 안내',
  ADMIN: '관리자',
  AMOUNT_MISMATCH: '금액 불일치',
  API_KEY: 'API 키',
  ACTIVATION: '최초 로그인',
  PASSWORD_RESET: '비밀번호 재설정',
  TERMINATION_RESULT: '해지 결과 확인',
  IDENTITY_VERIFICATION: '본인인증',
  DANAL: '다날',
  KG_FINANCIAL: 'KG모빌리언스',
  PAYLETTER: '페이레터',
  HECTO: '헥토파이낸셜',
  BIZPPURIO: '비즈뿌리오',
  INITIAL: '최초 결제',
  RECURRING: '정기 결제',
  PRORATED: '상품 변경 차액',
  PARTNER_JOIN_INITIAL: '제휴 가입 최초 결제',
  SUBSCRIPTION_INITIAL: '월정액 최초 결제',
  SUBSCRIPTION_RECURRING: '월정액 정기 결제',
  PLAN_CHANGE_PRORATED: '상품 변경 차액 결제',
  TRANSACTION_INITIAL: '거래 최초 결제',
  CUSTOMER_REFUND: '고객 환불',
  TRANSACTION_TRANSFER: '거래 대금 송금',
  PAYOUT: '거래 대금 송금',
  CARD: '카드',
  MOBILE: '휴대폰',
  BANK_TRANSFER: '계좌 이체',
  CS: '고객지원 담당자',
  BENEFIT: '혜택 지원',
  DIRECT: '직접 가입',
  EPOCH: '에포크',
  LOCAL: '자체 연동',
  LIVE: '실제',
  MAINTENANCE: '점검 안내',
  MEMBER: '회원',
  MEMBER_SENSITIVE_REVEALED: '회원 민감정보 열람',
  OPERATOR: '운영 담당자',
  OUTBOUND: '발신',
  PARTNER_JOIN: '제휴 가입',
  PAYMENT: '결제',
  PAYMENT_APPROVED: '결제 승인',
  PG_CANCEL: 'PG 결제 취소',
  PRODUCTION: '운영',
  PROVIDER_TIMEOUT: '제공자 응답 지연',
  REFUND: '환불',
  RENT: '임대료',
  RESPONSE: '응답',
  REVIEWER: '심사 담당자',
  SANDBOX: '샌드박스',
  SERVICE: '서비스',
  SERVICE_NOTICE: '서비스 안내',
  SETTLEMENT: '정산',
  SMS: '문자',
  ALIMTALK: '알림톡',
  IN_APP_ONLY: '화면 알림만 제공',
  ACCEPTED: '발송 요청 접수',
  SENT: '발송 요청 접수',
  SUPER: '시스템 관리자',
  TEXT: '텍스트',
  TIMEOUT: '시간 초과',
  TRANSACTION: '거래',
  TRANSACTION_CANCELED: '거래 취소',
  TRANSACTION_PAYOUT: '거래 대금',
  LIMIT_RESTORED: '한도 복원',
  TRANSFER: '송금',
  VIEWER: '조회 담당자',
};
export const businessCodeLabel = (code: string | null | undefined) => code ? businessCodeLabels[code] ?? code.replaceAll('_',' ') : '—';
